import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link, useNavigate } from 'react-router-dom';
import { createPageUrl } from '@/utils';
import { Badge } from "@/components/ui/badge";
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Lightbulb, TrendingUp, TrendingDown, Building2, User as UserIcon, RefreshCw, Zap, ShoppingCart, AlertTriangle, Calendar, Info, Mail, Linkedin, Phone, ExternalLink, Search, Loader2 } from 'lucide-react';
import { formatDistanceToNow, differenceInDays, subWeeks, isValid as isValidDate } from 'date-fns';
import MobileTooltip from '@/components/MobileTooltip';
import { useUserContext, useUser } from '@/contexts/UserContext';
import { resolveEffectiveTenant } from '@/lib/tenantUtils';
import { displayPersonaScore, formatPersonaScore, isPersonaReportingScore } from '@/lib/personaSignalScore';
import { notify } from '@/components/ui/notify';
import { logAccountInteraction } from '@/components/utils/analytics';
import { useTranslation } from '@/components/utils/translations';
import { expandAlertCategoriesForMatching } from '@/lib/alertCategoryNames';
import {
  buildPersonasTabPagePath,
  getResearchItemSubtitle,
  isPlanResearchItem,
  planItemKey,
  planItemWhy,
  sortPlanCategoryEntries,
  translatePlanCategory,
} from '@/lib/todaysPlanUtils';
import PlanPriorityBadge from '@/components/home/PlanPriorityBadge';
import {
  openRecommendationComposeDraft,
  RecommendationComposeNotFoundError,
  resolveRecommendationId,
} from '@/lib/chatbotBrief';
import { buildComposeViaPersonaPagePath } from '@/lib/chatbotDeepLinks';

const API_BASE_URL = (() => {
  if (typeof window !== 'undefined') {
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      return 'https://api.neogtm.com';
    }
  }
  return 'https://api.neogtm.com';
})();

const CHANNEL_ICONS = {
  email: Mail,
  linkedin: Linkedin,
  call: Phone
};

const getChannelLabel = (channel, t) => {
  const labels = {
    email: t?.('email') || 'Email',
    linkedin: t?.('linkedin') || 'LinkedIn',
    call: t?.('call') || 'Call'
  };
  return labels[channel] || channel;
};

const safeFormatDate = (dateValue, t) => {
  if (!dateValue) return t?.('recently') || 'recently';
  try {
    const date = new Date(dateValue);
    if (!isValidDate(date)) return t?.('recently') || 'recently';
    return formatDistanceToNow(date, { addSuffix: true });
  } catch (e) {
    return t?.('recently') || 'recently';
  }
};

const MomentumItem = ({ update, t }) => {
    const isPositive = update.score_change >= 0;
    
    return (
        <div className="block p-3 rounded-lg hover:bg-white transition-colors -mx-3 px-3">
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${isPositive ? 'bg-green-100' : 'bg-red-100'}`}>
                        {isPositive ? <TrendingUp className="w-4 h-4 text-green-600" /> : <TrendingDown className="w-4 h-4 text-red-600" />}
                    </div>
                    <div>
                        {update.source_url ? (
                            <a 
                                href={update.source_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-medium text-[#0A1A2F] hover:text-[#00C2C2] underline decoration-[#00C2C2]"
                            >
                                {update.title}
                            </a>
                        ) : (
                            <p className="text-sm font-medium text-[#0A1A2F]">{update.title}</p>
                        )}
                        <div className="flex items-center gap-2 text-xs text-[#5C6B7A] mt-1">
                            <Building2 className="w-3 h-3"/>
                            <Link to={update.link} className="hover:text-[#00C2C2]">
                                {update.name}
                            </Link>
                            <span>&bull;</span>
                            <span>{safeFormatDate(update.date_published, t)}</span>
                        </div>
                    </div>
                </div>
                <div className={`text-sm font-bold ${isPositive ? 'text-green-600' : 'text-red-600'}`}>
                    {isPositive ? `+${update.score_change}` : update.score_change}
                </div>
            </div>
        </div>
    );
};

const PersonaItem = ({ update, t }) => {
    const isPositive = (update.score_change ?? 0) >= 0;
    return (
        <div className="block p-3 rounded-lg hover:bg-white transition-colors -mx-3 px-3">
            <div className="flex items-start justify-between gap-4">
                <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-full ${isPositive ? 'bg-purple-100' : 'bg-amber-100'}`}>
                        <UserIcon className={`w-4 h-4 ${isPositive ? 'text-purple-600' : 'text-amber-700'}`} />
                    </div>
                    <div>
                        {update.source_url ? (
                            <a 
                                href={update.source_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-sm font-medium text-[#0A1A2F] hover:text-purple-600 underline decoration-purple-500"
                            >
                                {update.title}
                            </a>
                        ) : (
                            <p className="text-sm font-medium text-[#0A1A2F]">{update.title}</p>
                        )}
                        <div className="flex items-center gap-2 text-xs text-[#5C6B7A] mt-1">
                            <UserIcon className="w-3 h-3"/>
                            <Link to={update.link} className="hover:text-[#00C2C2]">
                                {update.name}
                            </Link>
                            <span>&bull;</span>
                            <span>{safeFormatDate(update.date_published, t)}</span>
                        </div>
                    </div>
                </div>
                <div className={`text-sm font-bold ${isPositive ? 'text-green-600' : 'text-amber-700'}`}>
                    {formatPersonaScore(update.score_change)}
                </div>
            </div>
        </div>
    );
};

function cleanWhyNow(text) {
  if (!text) return text;
  return text
    .replace(/;\s*high fit\s*\([^)]+\)/i, '')
    .replace(/;\s*decision window\s*[><]\s*\d+\s*months?\.?/i, '')
    .trim();
}

export default function ActionCenterWithPlan({ 
    momentumScores, 
    personaActivities,
    procurement,
    plan,
    updatedAt,
    isRefreshing,
    onRefresh,
    onAcknowledge,
    startChatSession,
    onTogglePursuit, 
    accounts,
    people,
    isPlanLoading,
    isHydrating,
    currentTenant,
    alertPreferences,
    onOpenDraft
}) {
    const { t } = useTranslation();
    const { id: userId, currentTenant: contextTenant } = useUserContext();
    const contextUser = useUser();
    const adminTenant =
      contextUser?.role === 'admin'
        ? localStorage.getItem('admin-active-tenant')
        : null;
    const effectiveTenant = resolveEffectiveTenant(
      adminTenant,
      currentTenant || contextTenant,
      contextUser,
      'instructure'
    );
    const [activeTab, setActiveTab] = useState("plan");
    const [planFilter, setPlanFilter] = useState("all");
    const [tasks, setTasks] = useState([]);
    const [loadingTasks, setLoadingTasks] = useState(false);
    const [composeLoadingKey, setComposeLoadingKey] = useState(null);
    const navigate = useNavigate();

    const loadTasks = async () => {
        if (!userId) return;
        setLoadingTasks(true);
        try {
            const response = await fetch(
                `${API_BASE_URL}/api/todays-plan/tasks?tenant_id=${effectiveTenant}&user_id=${userId}`,
                { credentials: 'include' }
            );

            if (response.ok) {
                const data = await response.json();
                setTasks(data);
            }
        } catch (error) {
            if (process.env.NODE_ENV === 'development') console.warn('ActionCenterWithPlan: Could not load tasks', error?.message);
        } finally {
            setLoadingTasks(false);
        }
    };

    useEffect(() => {
        loadTasks();
    }, [effectiveTenant, userId]);

    const handleCompleteTask = async (taskId) => {
        if (!userId) return;
        try {
            const tenantId = effectiveTenant;
            const completedTask = tasks.find(t => t.id === taskId);
            if (!completedTask) {
                throw new Error('Task not found');
            }

            const url = `${API_BASE_URL}/api/todays-plan/tasks/${taskId}/complete`;
            const payload = {
                tenant_id: tenantId,
                user_id: userId,
                description: t('completedFromTodaysPlan')
            };

            const response = await fetch(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                // Track task completion
                logAccountInteraction({
                    tenantId: effectiveTenant,
                    userId,
                    accountId: completedTask.account_id,
                    accountName: completedTask.account_name,
                    personId: completedTask.person_id,
                    personName: completedTask.person_name,
                    stepId: completedTask.step_id,
                    channel: completedTask.channel,
                    source: 'today_plan',
                    interactionType: 'completed',
                    touchNumber: completedTask.touch_number
                }).catch(err => console.error('Analytics error:', err));
                
                setTasks(prev => prev.filter(t => t.id !== taskId));
                notify({
                    title: t('taskCompletedTitle'),
                    description: t('taskCompletedDescription'),
                });
            } else {
                const errorText = await response.text();
                throw new Error(`Failed to complete task: ${errorText}`);
            }
        } catch (error) {
            console.error('Error completing task:', error);
            notify({
                title: t('error'),
                description: error.message || t('taskCompleteError'),
                variant: 'destructive'
            });
        }
    };

    const getAccountNameForTask = (task) => {
        if (task.account_name && task.account_name !== t('unknownAccount') && task.account_name.trim() !== '') {
            return task.account_name;
        }

        let matchedPerson = null;
        
        if (task.person_id && people) {
            matchedPerson = people.find(p => p.id === task.person_id || p.person_id === task.person_id);
        }
        
        if (!matchedPerson && task.person_name && people) {
            const taskPersonName = task.person_name.toLowerCase().trim();
            matchedPerson = people.find(p => {
                const fullName = `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase().trim();
                const altFullName = p.full_name?.toLowerCase().trim();
                return fullName === taskPersonName || altFullName === taskPersonName;
            });
        }

        if (matchedPerson && matchedPerson.account_name) {
            return matchedPerson.account_name;
        }

        return t('unknownAccount');
    };

    const getPersonaName = (item) => {
        if (typeof item.personaName === 'string' && item.personaName.trim() !== '') {
            return item.personaName;
        }
        
        const personaId = item.personaId || item.person_id || item.persona_id;
        if (personaId && people) {
            const matchedPerson = people.find(p => 
                p.id === personaId || p.person_id === personaId
            );
            if (matchedPerson) {
                return `${matchedPerson.first_name || ''} ${matchedPerson.last_name || ''}`.trim();
            }
        }
        
        if (typeof item.personaName === 'string' && item.personaName.trim() !== '' && people) {
            const itemName = item.personaName.toLowerCase().trim();
            const matchedPerson = people.find(p => {
                const fullName = `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase().trim();
                const altFullName = p.full_name?.toLowerCase().trim();
                return fullName === itemName || altFullName === itemName;
            });
            if (matchedPerson) {
                return `${matchedPerson.first_name || ''} ${matchedPerson.last_name || ''}`.trim();
            }
        }

        return t('unknownContact');
    };

    const handleOpenPlanDraft = async (item, { finalPersonId, accountIdForLink, whyText }) => {
        if (!userId || !effectiveTenant) return;

        const recommendationId = resolveRecommendationId(item);
        const itemKey = planItemKey(item);
        setComposeLoadingKey(itemKey);
        try {
            let pagePath = null;

            if (recommendationId) {
                pagePath = await openRecommendationComposeDraft({
                    tenantId: effectiveTenant,
                    userId,
                    recommendationId,
                });
            } else {
                const briefParts = [
                    item.title,
                    item.why_now,
                    whyText ? cleanWhyNow(whyText) : null,
                    item.nextSteps,
                ].filter(Boolean);
                const composeBrief = briefParts.join('. ').trim();

                pagePath = buildComposeViaPersonaPagePath({
                    personId: finalPersonId,
                    accountId: accountIdForLink,
                    composeBrief,
                    autoGenerate: true,
                });
            }

            if (!pagePath) {
                throw new Error('No persona target for this recommendation');
            }

            navigate(createPageUrl(pagePath));
        } catch (err) {
            console.error('Open plan draft failed:', err);
            if (err instanceof RecommendationComposeNotFoundError || err?.status === 404) {
                notify({
                    title: t('error'),
                    description: t('noContactOnFileOpenPersonas'),
                    variant: 'destructive',
                });
            } else {
                notify({
                    title: t('error'),
                    description: err?.message || t('pleaseTryAgain'),
                    variant: 'destructive',
                });
            }
        } finally {
            setComposeLoadingKey(null);
        }
    };

    const topMomentum = useMemo(() => {
        const twoWeeksAgo = subWeeks(new Date(), 2);
        
        const shouldFilterToEdqwest = effectiveTenant === 'faria' || effectiveTenant === 'edqwest';
        
        return (momentumScores || [])
            .filter(item => {
                const dateStr = item.date_published || item.weekly_period;
                if (!dateStr) return false;
                
                try {
                    const itemDate = new Date(dateStr);
                    if (!isValidDate(itemDate)) return false;
                    
                    const isRecent = itemDate >= twoWeeksAgo;
                    if (!isRecent) return false;
                    
                    if (shouldFilterToEdqwest) {
                        return item.tenant_id === 'edqwest';
                    }
                    
                    return true;
                } catch (e) {
                    return false;
                }
            })
            .map(item => ({
                id: `momentum_${item.id}`,
                type: 'account',
                title: item.summary || `Momentum change: ${item.institution_name}`,
                name: item.institution_name,
                score_change: item.momentum_score_change != null
                  ? item.momentum_score_change
                  : item.signal_score,
                date: item.date_published || item.weekly_period,
                link: createPageUrl(`AccountFinal?id=${item.account_id || item.institution_name}`),
                source_url: item.source_url
            }))
            .sort((a, b) => Math.abs(b.score_change) - Math.abs(a.score_change))
            .slice(0, 5);
    }, [momentumScores, effectiveTenant]);

    const topPersonas = useMemo(() => {
        const twoWeeksAgo = subWeeks(new Date(), 2);
        
        const shouldFilterToEdqwest = effectiveTenant === 'faria' || effectiveTenant === 'edqwest';
        
        return (personaActivities || [])
            .filter(item => {
                // Check date_published first, fallback to date
                const dateStr = item.date_published || item.date;
                if (!dateStr) return false;
                
                try {
                    const itemDate = new Date(dateStr);
                    if (!isValidDate(itemDate)) return false;
                    
                    const isRecent = itemDate >= twoWeeksAgo;
                    if (!isRecent) return false;
                    
                    if (shouldFilterToEdqwest) {
                        return item.tenant_id === 'edqwest';
                    }
                    
                    return true;
                } catch (e) {
                    return false;
                }
            })
            .map(item => ({
                id: `persona_${item.id}`,
                type: 'persona',
                title: item.summary,
                name: item.full_name || `${item.first_name} ${item.last_name}`,
                score_change: displayPersonaScore(item),
                date: item.date_published,
                // Prefer person_id, fallback to id for tenant datasets that only populate one field.
                link: (item.person_id || item.id) ? createPageUrl(`PersonProfile?id=${encodeURIComponent(item.person_id || item.id)}`) : null,
                source_url: item.source_url || item.full_url
            }))
            .filter(item => item.link !== null && isPersonaReportingScore(item.score_change))
            .sort((a, b) => Math.abs(b.score_change) - Math.abs(a.score_change))
            .slice(0, 5);
    }, [personaActivities, effectiveTenant]);

        // DEBUG: Log signal filtering
    useEffect(() => {
        console.log('🔍 SIGNALS DEBUG:', {
          momentumScoresReceived: momentumScores?.length || 0,
          personaActivitiesReceived: personaActivities?.length || 0,
          topMomentumFiltered: topMomentum.length,
          topPersonasFiltered: topPersonas.length,
          twoWeeksAgo: subWeeks(new Date(), 2).toISOString(),
          sampleMomentumDates: (momentumScores || []).slice(0, 5).map(m => m.date_published || m.weekly_period),
          samplePersonaDates: (personaActivities || []).slice(0, 5).map(p => p.date)
        });
    }, [momentumScores, personaActivities, topMomentum, topPersonas]);
    const urgentDeals = useMemo(() => {
        console.log('🎯 ACTION CENTER urgentDeals INPUT:', {
            procurementLength: procurement?.length || 0,
            alertPreferences: alertPreferences ? 'present' : 'null',
            selectedCategories: alertPreferences?.live_deal_categories || []
        });
    
        if (!procurement || procurement.length === 0) {
            console.log('🎯 ACTION CENTER: No procurement data');
            return [];
        }
      
        const today = new Date();
        today.setHours(0, 0, 0, 0);
      
        // FIRST: Filter for Live deals only (like Market page does)
        let deals = procurement.filter(p => p.status === 'Live');
        
        console.log('🎯 ACTION CENTER: After Live filter:', {
            before: procurement.length,
            after: deals.length
        });
      
        // Filter by alert preferences categories
        if (alertPreferences?.live_deal_categories?.length > 0) {
            const normalizeCategory = (cat) => {
                if (!cat) return null;
                return cat.replace(/[\u2010\u2011\u2012\u2013\u2014\u2015]/g, '-').trim();
            };
      
            const normalizedCategories = expandAlertCategoriesForMatching(alertPreferences.live_deal_categories).map(c => normalizeCategory(c));
      
            deals = deals.filter(p => {
                const dealCategory = normalizeCategory(p.category);
                return dealCategory && normalizedCategories.includes(dealCategory);
            });
    
            console.log('🎯 ACTION CENTER: After category filter:', {
                before: procurement.filter(p => p.status === 'Live').length,
                after: deals.length,
                categories: normalizedCategories
            });
        }
      
        // Filter out expired deals, but keep N/A deadlines
        const filtered = deals.filter(p => {
            if (!p.deadline || p.deadline === 'N/A' || p.deadline.trim?.() === '') {
                return true;
            }
      
            try {
                const deadlineDate = new Date(p.deadline);
                if (isNaN(deadlineDate.getTime())) {
                    return true;
                }
                deadlineDate.setHours(0, 0, 0, 0);
                return deadlineDate >= today;
            } catch (e) {
                return true;
            }
        });
    
        console.log('🎯 ACTION CENTER: After deadline filter:', {
            before: deals.length,
            after: filtered.length,
            todayDate: today.toISOString()
        });
      
        const result = filtered
            .sort((a, b) => {
                const aHasDeadline = a.deadline && a.deadline !== 'N/A' && a.deadline.trim?.() !== '';
                const bHasDeadline = b.deadline && b.deadline !== 'N/A' && b.deadline.trim?.() !== '';
      
                if (aHasDeadline && !bHasDeadline) return -1;
                if (!aHasDeadline && bHasDeadline) return 1;
                if (!aHasDeadline && !bHasDeadline) return 0;
      
                try {
                    return new Date(a.deadline) - new Date(b.deadline);
                } catch {
                    return 0;
                }
            })
            .slice(0, 5)
            .map(p => ({
                title: p.title,
                institution: p.account_name || p.institution_name || p.institution,
                deadline: p.deadline,
                value: p.estimated_value,
                currency: p.currency,
                category: p.category,
                source_url: p.source_url,
                link: p.source_url || createPageUrl('Procurement')
            }));
    
        console.log('🎯 ACTION CENTER urgentDeals OUTPUT:', {
            finalCount: result.length,
            titles: result.map(d => d.title)
        });
    
        return result;
    }, [procurement, alertPreferences]);

    const nearTermAccounts = useMemo(() => {
        return (accounts || [])
            .filter(acc => acc.decision_window_band === '<12m' || acc.decision_window_band === '<12 months')
            .sort((a,b) => b.account_fit_score - a.account_fit_score)
            .slice(0, 5);
    }, [accounts]);

    const categories = useMemo(
      () => sortPlanCategoryEntries(Object.entries(plan || {})),
      [plan],
    );
    const hasItems = categories.length > 0;
    const totalPlanItems = categories.reduce((sum, [, items]) => sum + items.length, 0);

    const showTasks = planFilter === 'all' || planFilter === 'tasks';
    const showPlanItems = planFilter === 'all' || planFilter === 'leads';

    return (
        <Card className="border-0 shadow-md overflow-hidden relative h-full bg-blue-100">
            <CardContent className="p-3 md:p-4 lg:p-6 relative z-10">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                    <div className="flex flex-col gap-3 mb-4">
                        <div className="flex items-center justify-between gap-2">
                            <div className="flex items-center gap-2 md:gap-3 flex-1 min-w-0">
                                <Lightbulb className="w-4 h-4 md:w-5 md:h-5 text-[#00C2C2] flex-shrink-0" />
                                <TabsList className="bg-white/60 border border-gray-200">
                                    <TabsTrigger 
                                        value="plan" 
                                        className="data-[state=active]:bg-white data-[state=active]:text-[#0A1A2F] data-[state=inactive]:bg-gray-100 data-[state=inactive]:text-[#5C6B7A] text-xs px-2 md:px-3 py-1.5"
                                    >
                                        <span className="flex items-center gap-1">
                                            {t('priorityActions')}
                                        </span>
                                        {(hasItems || tasks.length > 0) && (
                                            <Badge variant="secondary" className="ml-1 md:ml-1.5 h-4 text-xs bg-[#00C2C2] text-white">
                                                {tasks.length + totalPlanItems}
                                            </Badge>
                                        )}
                                    </TabsTrigger>
                                    <TabsTrigger 
                                        value="signals" 
                                        className="data-[state=active]:bg-white data-[state=active]:text-[#0A1A2F] data-[state=inactive]:bg-gray-100 data-[state=inactive]:text-[#5C6B7A] text-xs px-2 md:px-3 py-1.5"
                                    >
                                        {t('signals')}
                                    </TabsTrigger>
                                    <TabsTrigger 
                                        value="deals" 
                                        className="data-[state=active]:bg-white data-[state=active]:text-[#0A1A2F] data-[state=inactive]:bg-gray-100 data-[state=inactive]:text-[#5C6B7A] text-xs px-2 md:px-3 py-1.5"
                                    >
                                        {t('deals')}
                                    </TabsTrigger>
                                </TabsList>
                            </div>
                            
                            {activeTab === 'plan' && (
                                <div className="hidden md:block">
                                    <Select value={planFilter} onValueChange={setPlanFilter}>
                                        <SelectTrigger className="w-28 h-7 text-xs bg-white/80 border-gray-200">
                                            <SelectValue placeholder={t('filter')} />
                                        </SelectTrigger>
                                        <SelectContent>
                                            <SelectItem value="all" className="text-xs">{t('all')}</SelectItem>
                                            <SelectItem value="tasks" className="text-xs">{t('myTasks')}</SelectItem>
                                            <SelectItem value="leads" className="text-xs">{t('newLeads')}</SelectItem>
                                        </SelectContent>
                                    </Select>
                                </div>
                            )}
                            
                            <Button 
                                variant="ghost" 
                                size="sm" 
                                onClick={onRefresh}
                                disabled={isRefreshing}
                                className="text-[#5C6B7A] hover:bg-white/60 h-7 px-2 flex-shrink-0"
                            >
                                {isRefreshing ? (
                                    <RefreshCw className="w-4 md:w-3.5 h-4 md:h-3.5 animate-spin" />
                                ) : (
                                    <RefreshCw className="w-4 md:w-3.5 h-4 md:h-3.5" />
                                )}
                            </Button>
                        </div>
                        
                        {activeTab === 'plan' && (
                            <div className="md:hidden">
                                <Select value={planFilter} onValueChange={setPlanFilter}>
                                    <SelectTrigger className="w-full h-9 text-sm bg-white/80 border-gray-200">
                                        <SelectValue placeholder={t('filter')} />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all" className="text-sm">{t('all')}</SelectItem>
                                        <SelectItem value="tasks" className="text-sm">{t('myTasks')}</SelectItem>
                                        <SelectItem value="leads" className="text-sm">{t('newLeads')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        )}
                        
                        {activeTab === 'plan' && updatedAt && (
                            <p className="text-xs text-[#5C6B7A]">
                                {t('updated')} {new Date(updatedAt).toLocaleString()}
                            </p>
                        )}
                    </div>

                    <TabsContent value="plan" className="mt-0 space-y-3">
                        {isPlanLoading ? (
                            <div className="flex flex-col items-center justify-center py-8 md:py-12">
                                <div className="relative">
                                    <div className="w-10 h-10 md:w-12 md:h-12 border-4 border-gray-200 border-t-[#00C2C2] rounded-full animate-spin"></div>
                                    <Lightbulb className="w-5 h-5 md:w-6 md:h-6 text-[#00C2C2] absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" />
                                </div>
                                <p className="mt-4 text-sm md:text-base font-medium text-[#0A1A2F]">{t('loadingRecommendations')}</p>
                                <p className="text-xs text-[#5C6B7A] mt-1">{t('analyzingPipeline')}</p>
                            </div>
                        ) : (
                            <>
                                {showTasks && tasks.length > 0 && (
                                    <div className="mb-3">
                                        <h3 className="font-medium text-[#0A1A2F] text-sm flex items-center gap-2 mb-2">
                                            <Calendar className="w-4 h-4" />
                                            {t('dueToday')}
                                            <Badge variant="secondary" className="h-4 text-xs bg-gray-200 text-[#5C6B7A]">{tasks.length}</Badge>
                                        </h3>
                                        <div className="space-y-2">
                                            {tasks.map((task) => {
                                                const ChannelIcon = CHANNEL_ICONS[task.channel];
                                                const accountName = getAccountNameForTask(task);
                                                
                                                return (
                                                    <div key={task.id} className="bg-white rounded-lg p-3 border border-gray-200 shadow-sm">
                                                        <div className="text-xs md:text-sm text-[#5C6B7A] mb-2">
                                                            <span className="font-semibold text-[#0A1A2F] break-words">{task.person_name}</span>
                                                            {' @ '}
                                                            <span className="font-medium break-words">{accountName}</span>
                                                        </div>
                                                        
                                                        <div className="text-xs md:text-sm text-[#0A1A2F] mb-2 leading-relaxed font-medium">
                                                            {t('completeTouchVia')} #{task.touch_number} {t('via')} {getChannelLabel(task.channel, t)}
                                                        </div>
                                                        
                                                        <div className="flex flex-wrap items-center gap-2 mb-3">
                                                            <Badge 
                                                                variant={
                                                                    task.channel === 'email' ? 'default' :
                                                                    task.channel === 'linkedin' ? 'secondary' :
                                                                    'outline'
                                                                } 
                                                                className="text-xs h-5 px-2 flex items-center gap-1"
                                                            >
                                                                {ChannelIcon && <ChannelIcon className="w-3 h-3" />}
                                                                {getChannelLabel(task.channel, t)}
                                                            </Badge>
                                                            <Badge className="bg-orange-100 text-orange-800 text-xs h-5 px-2">{t('dueToday')}</Badge>
                                                        </div>
                                                        
                                                        <div className="flex flex-wrap gap-2">
                                                            {task.channel === 'email' && (
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => onOpenDraft?.(task.step_id, task)}
                                                                    className="text-xs px-2 py-1 bg-white border-gray-300 text-[#0A1A2F] hover:bg-gray-50"
                                                                >
                                                                    {t('openDraft')}
                                                                </Button>
                                                            )}
                                                            {task.channel === 'linkedin' && task.linkedin_url && (
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => onOpenDraft?.(task.step_id, task)}
                                                                    className="text-xs px-2 py-1 bg-white border-gray-300 text-[#0A1A2F] hover:bg-gray-50"
                                                                >
                                                                    <ExternalLink className="w-3 h-3 mr-1" />
                                                                    {t('openLinkedIn')}
                                                                </Button>
                                                            )}
                                                            {task.channel === 'call' && task.phone && (
                                                                <Button
                                                                    variant="outline"
                                                                    size="sm"
                                                                    onClick={() => onOpenDraft?.(task.step_id, task)}
                                                                    className="text-xs px-2 py-1 bg-white border-gray-300 text-[#0A1A2F] hover:bg-gray-50"
                                                                >
                                                                    <Phone className="w-3 h-3 mr-1" />
                                                                    {t('callNow')}
                                                                </Button>
                                                            )}
                                                            <button
                                                                type="button"
                                                                className="text-xs px-2 py-1 rounded-md border bg-white border-gray-300 text-[#0A1A2F] hover:bg-gray-50 transition-colors"
                                                                onClick={() => handleCompleteTask(task.id)}
                                                            >
                                                                {t('done')}
                                                            </button>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {showPlanItems && hasItems ? (
                                    <div className="space-y-3">
                                        {categories.map(([category, items]) => (
                                            <div key={category}>
                                                <h3 className="font-medium text-[#0A1A2F] text-sm flex items-center gap-2 mb-2">
                                                    {translatePlanCategory(category, t)} <Badge variant="secondary" className="h-4 text-xs bg-gray-200 text-[#5C6B7A]">{items.length}</Badge>
                                                </h3>
                                                
                                                <div className="space-y-2">
                                                    {items.map((item, idx) => {
                                                        const isResearch = isPlanResearchItem(item);
                                                        const tracked = item.metadata?.addToPursuit;
                                                        const fitScore = item.accountFitScore || null;
                                                        const whyText = planItemWhy(item);
                                                        const researchSubtitle = isResearch
                                                          ? getResearchItemSubtitle(item, t)
                                                          : null;
                                                        
                                                        const personaIdForLink = item.personaId || item.person_id || item.persona_id;
                                                        
                                                        const matchedPersona = people?.find(p => {
                                                            if (personaIdForLink && (p.id === personaIdForLink || p.person_id === personaIdForLink)) {
                                                                return true;
                                                            }
                                                            
                                                            if (item.personaName) {
                                                                const itemName = item.personaName.toLowerCase().trim();
                                                                const fullName = `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase().trim();
                                                                const altFullName = p.full_name?.toLowerCase().trim();
                                                                
                                                                return fullName === itemName || altFullName === itemName;
                                                            }
                                                            
                                                            return false;
                                                        });
                                                        
                                                        const matchedAccount = accounts?.find(a => {
                                                            if (item.accountId && (a.id === item.accountId || a.account_id === item.accountId)) {
                                                                return true;
                                                            }
                                                            
                                                            if (item.accountName) {
                                                                const itemAccName = item.accountName.toLowerCase().trim();
                                                                const accName = a.account_name?.toLowerCase().trim();
                                                                return accName === itemAccName;
                                                                
                                                            }
                                                            
                                                            return false;
                                                        });
                                                        
                                                        const finalPersonId = matchedPersona?.person_id || matchedPersona?.id || personaIdForLink;
                                                        const displayName = getPersonaName(item);
                                                        const accountIdForLink =
                                                          item.accountId ||
                                                          item.account_id ||
                                                          matchedAccount?.account_id ||
                                                          matchedAccount?.id;
                                                        const personasPagePath = buildPersonasTabPagePath(accountIdForLink);

                                                        const itemKey = planItemKey(item);
                                                        const recommendationId = resolveRecommendationId(item);
                                                        const canOpenDraft = Boolean(
                                                            finalPersonId ||
                                                            accountIdForLink ||
                                                            recommendationId,
                                                        );

                                                        return (
                                                            <div
                                                                key={idx}
                                                                className={`rounded-lg p-3 border shadow-sm ${
                                                                  isResearch
                                                                    ? 'bg-slate-50 border-slate-200'
                                                                    : 'bg-white border-gray-200'
                                                                }`}
                                                            >
                                                                {isResearch ? (
                                                                  <>
                                                                    <div className="flex items-start gap-2 mb-2">
                                                                      <Search className="w-3.5 h-3.5 text-[#7A8694] shrink-0 mt-0.5" aria-hidden />
                                                                      <div className="min-w-0 flex-1">
                                                                        <p className="text-sm font-medium text-[#0A1A2F] leading-snug">{item.title}</p>
                                                                        {researchSubtitle ? (
                                                                          <p className="text-xs text-[#5C6B7A] mt-1 leading-relaxed">{researchSubtitle}</p>
                                                                        ) : null}
                                                                      </div>
                                                                      {fitScore ? (
                                                                        <Badge variant="outline" className="text-xs h-5 px-2 shrink-0 bg-purple-50 border-purple-200 text-purple-800">
                                                                          {t('todaysPlanAccountFit')}: {fitScore}
                                                                        </Badge>
                                                                      ) : null}
                                                                    </div>
                                                                    <div className="flex flex-wrap gap-2">
                                                                      {personasPagePath ? (
                                                                        <Button
                                                                          variant="outline"
                                                                          size="sm"
                                                                          onClick={() => navigate(createPageUrl(personasPagePath))}
                                                                          className="text-xs px-2 py-1 bg-white border-gray-300 text-[#0A1A2F] hover:bg-gray-50"
                                                                        >
                                                                          {t('openPersonas')}
                                                                        </Button>
                                                                      ) : null}
                                                                      {onAcknowledge ? (
                                                                        <button
                                                                          type="button"
                                                                          className="text-xs px-2 py-1 rounded-md border bg-white border-gray-300 text-[#0A1A2F] hover:bg-gray-50 transition-colors"
                                                                          onClick={() => onAcknowledge({ ...item, personaId: null, person_id: null })}
                                                                        >
                                                                          {t('done')}
                                                                        </button>
                                                                      ) : null}
                                                                    </div>
                                                                  </>
                                                                ) : (
                                                                  <>
                                                                <div className="text-xs md:text-sm text-[#5C6B7A] mb-2">
                                                                    {finalPersonId ? (
                                                                        <>
                                                                            <Link
                                                                                to={createPageUrl(`PersonProfile?id=${encodeURIComponent(finalPersonId)}`)}
                                                                                className="text-[#00C2C2] hover:text-[#00A3A3] hover:underline font-semibold break-words"
                                                                            >
                                                                                {displayName}
                                                                            </Link>
                                                                            {' @ '}
                                                                            <span className="font-medium break-words">{item.accountName}</span>
                                                                        </>
                                                                    ) : (
                                                                        <><span className="font-semibold break-words">{displayName}</span> @ <span className="font-medium break-words">{item.accountName}</span></>
                                                                    )}
                                                                </div>
                                                                
                                                                <div className="text-xs md:text-sm text-[#0A1A2F] mb-2 leading-relaxed font-medium">
                                                                  <span>{item.title}</span>
                                                                </div>
                                                                
                                                                {whyText && (
                                                                    <div className="text-xs text-[#5C6B7A] mb-2 leading-relaxed break-words">
                                                                          <>
                                                                            <span className="font-semibold text-[#0A1A2F]">{t('whyNow')}</span>{' '}
                                                                            {cleanWhyNow(whyText)}
                                                                          </>
                                                                    </div>
                                                                )}

                                                                {item.nextSteps && (
                                                                    <div className="text-xs text-[#5C6B7A] mb-2 leading-relaxed">
                                                                        <span className="font-semibold">{t('nextSteps')}</span> {item.nextSteps}
                                                                    </div>
                                                                )}
                                                                
                                                                <div className="flex flex-wrap items-center gap-2 mb-3">
                                                                      <PlanPriorityBadge item={item} className="px-2" />
                                                                    {tracked && <Badge className="bg-amber-100 text-amber-800 text-xs h-5 px-2">{t('tracked')}</Badge>}
                                                                    {fitScore && (
                                                                        <Badge variant="outline" className="text-xs h-5 px-2 bg-purple-50 border-purple-200 text-purple-800">
                                                                           {t('todaysPlanAccountFit')}: {fitScore}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                                
                                                                <div className="flex flex-wrap gap-2">
                                                                   {canOpenDraft ? (
                                                                       <Button
                                                                           variant="outline"
                                                                           size="sm"
                                                                           disabled={composeLoadingKey === itemKey}
                                                                           onClick={() => handleOpenPlanDraft(item, {
                                                                               finalPersonId,
                                                                               accountIdForLink,
                                                                               whyText,
                                                                           })}
                                                                           className="text-xs px-2 py-1 bg-white border-gray-300 text-[#0A1A2F] hover:bg-gray-50"
                                                                       >
                                                                           {composeLoadingKey === itemKey ? (
                                                                               <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                                           ) : null}
                                                                           {t('openDraft')}
                                                                       </Button>
                                                                   ) : null}
                                                                    {onAcknowledge && (
                                                                       <button
                                                                           type="button"
                                                                           className="text-xs px-2 py-1 rounded-md border bg-white border-gray-300 text-[#0A1A2F] hover:bg-gray-50 transition-colors"
                                                                           onClick={() => onAcknowledge({
                                                                               ...item,
                                                                               person_id: item.person_id || finalPersonId,
                                                                           })}
                                                                       >
                                                                           {t('done')}
                                                                       </button>
                                                                    )}
                                                                </div>
                                                                  </>
                                                                )}
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                ) : (!showTasks || !tasks.length) && (!showPlanItems || !hasItems) ? (
                                    <div className="text-center py-8 text-[#5C6B7A]">
                                        <Zap className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                        <p className="text-sm">
                                            {planFilter === 'tasks' && !tasks.length 
                                                ? t('noTasksDue')
                                                : planFilter === 'leads' && !hasItems
                                                ? t('noNewLeads')
                                                : t('noActionsYet')}
                                        </p>
                                        <p className="text-xs mt-1">{t('hitRefresh')}</p>
                                    </div>
                                ) : null}
                            </>
                        )}
                    </TabsContent>

                    <TabsContent value="signals" className="mt-0">
                        {isHydrating ? (
                            <div className="h-40 bg-white rounded-md animate-pulse" />
                        ) : (
                            <div className="space-y-4">
                                {topMomentum.length > 0 && (
                                    <div>
                                        <h3 className="text-xs font-semibold text-[#5C6B7A] mb-2 uppercase tracking-wide">{t('hotMovers')}</h3>
                                        <div className="divide-y divide-gray-200">
                                            {topMomentum.map(update => (
                                                <div key={update.id} className="py-2 first:pt-0">
                                                    <MomentumItem update={update} t={t} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {topPersonas.length > 0 && (
                                    <div>
                                        <h3 className="text-xs font-semibold text-[#5C6B7A] mb-2 uppercase tracking-wide">{t('personaUpdates')}</h3>
                                        <div className="divide-y divide-gray-200">
                                            {topPersonas.map(update => (
                                                <div key={update.id} className="py-2 first:pt-0">
                                                    <PersonaItem update={update} t={t} />
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}
                                
                                {topMomentum.length === 0 && topPersonas.length === 0 && (
                                    <div className="text-center py-8 text-[#5C6B7A]">
                                        <Zap className="w-8 h-8 mx-auto mb-2 opacity-50" />
                                        <p className="text-sm">{t('noSignals2Weeks')}</p>
                                    </div>
                                )}
                            </div>
                        )}
                    </TabsContent>

                    <TabsContent value="deals" className="mt-0 space-y-4">
                        {isHydrating ? (
                            <div className="h-40 bg-white rounded-md animate-pulse" />
                        ) : (
                            <>
                                {urgentDeals.length > 0 ? (
                                    <div>
                                        <h3 className="text-sm font-semibold text-[#0A1A2F] mb-2">{t('liveDealsTitle')}</h3>
                                        <div className="space-y-2">
                                            {urgentDeals.map((deal, idx) => {
                                                const today = new Date();
                                                today.setHours(0, 0, 0, 0);
                                                
                                                const hasDeadline = deal.deadline && deal.deadline !== 'N/A' && deal.deadline.trim() !== '';
                                                let daysLeft = null;
                                                
                                                if (hasDeadline) {
                                                    try {
                                                        const deadlineDate = new Date(deal.deadline);
                                                        deadlineDate.setHours(0, 0, 0, 0);
                                                        daysLeft = differenceInDays(deadlineDate, today);
                                                    } catch (e) {
                                                        console.warn('Invalid deadline:', deal.deadline);
                                                    }
                                                }

                                                return (
                                                    <a 
                                                        href={deal.link}
                                                        target={deal.source_url ? "_blank" : "_self"}
                                                        rel={deal.source_url ? "noopener noreferrer" : undefined}
                                                        key={`${deal.title}-${deal.institution}-${idx}`} 
                                                        className="block p-3 bg-orange-50/50 border border-orange-200 rounded-lg hover:bg-orange-50 transition-colors"
                                                    >
                                                        <div className="flex items-start justify-between gap-2">
                                                            <div className="flex-1 min-w-0">
                                                                <p className="font-medium text-sm text-orange-900 truncate">{deal.title}</p>
                                                                <p className="text-xs text-orange-700 mt-0.5">{deal.institution}</p>
                                                                {deal.category && (
                                                                    <p className="text-xs text-orange-600 mt-0.5">{deal.category}</p>
                                                                )}
                                                            </div>
                                                            {!hasDeadline ? (
                                                                <Badge className="bg-gray-500 text-white flex-shrink-0">
                                                                    {t('noDeadline')}
                                                                </Badge>
                                                            ) : daysLeft !== null ? (
                                                                daysLeft >= 0 ? (
                                                                    <Badge className="bg-red-500 text-white flex-shrink-0">
                                                                        <AlertTriangle className="w-3 h-3 mr-1" />
                                                                        {daysLeft}{t('daysLeft')}
                                                                    </Badge>
                                                                ) : (
                                                                    <Badge className="bg-gray-500 text-white flex-shrink-0">
                                                                        {t('expired')}
                                                                    </Badge>
                                                                )
                                                            ) : (
                                                                <Badge className="bg-gray-500 text-white flex-shrink-0">
                                                                    {t('noDeadline')}
                                                                </Badge>
                                                            )}
                                                        </div>
                                                    </a>
                                                );
                                            })}
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center py-6 text-[#5C6B7A] bg-gray-50/50 rounded-lg">
                                        <ShoppingCart className="w-6 h-6 mx-auto mb-2 opacity-50" />
                                        <p className="text-sm">{t('noLiveDealsFound')}</p>
                                    </div>
                                )}

                                <div>
                                    <div className="flex items-center gap-2 mb-2">
                                        <h3 className="text-sm font-semibold text-[#0A1A2F]">{t('decisionWindowRadar')}</h3>
                                        <MobileTooltip 
                                          content={<p className="text-sm">{t('decisionWindowTooltip')}</p>}
                                          side="bottom"
                                          align="start"
                                          className="bg-[#0A1A2F] text-white border-0 shadow-lg"
                                        >
                                            <button className="p-1 rounded-full hover:bg-gray-100 transition-colors touch-manipulation">
                                                <Info className="w-3.5 h-3.5 text-[#5C6B7A]" />
                                            </button>
                                        </MobileTooltip>
                                    </div>
                                    {nearTermAccounts.length > 0 ? (
                                        <div className="space-y-2">
                                            {nearTermAccounts.map(account => {
                                                return (
                                                    <Link to={createPageUrl(`AccountFinal?id=${account.id}`)} key={account.id} className="block p-2.5 bg-blue-50/50 border border-blue-200 rounded-lg hover:bg-blue-50 transition-colors">
                                                        <div className="flex items-center justify-between gap-3">
                                                            <div className="flex items-center gap-2 min-w-0 flex-1">
                                                                {account.logo_url ? (
                                                                    <img src={account.logo_url} alt={account.account_name} className="w-7 h-7 object-contain rounded-md border p-0.5 bg-white flex-shrink-0"/>
                                                                ) : (
                                                                    <div className="w-7 h-7 flex items-center justify-center bg-gray-200 rounded-md flex-shrink-0">
                                                                        <Building2 className="w-4 h-4 text-gray-500"/>
                                                                    </div>
                                                                )}
                                                                <p className="font-medium text-sm text-[#0A1A2F] truncate">{account.account_name}</p>
                                                            </div>
                                                            <Badge className="bg-blue-100 text-blue-800 text-xs flex-shrink-0">{account.decision_window_band}</Badge>
                                                        </div>
                                                    </Link>
                                                );
                                            })}
                                        </div>
                                    ) : (
                                        <div className="text-center py-6 text-[#5C6B7A] bg-gray-50/50 rounded-lg">
                                            <Calendar className="w-6 h-6 mx-auto mb-2 opacity-50" />
                                            <p className="text-sm">{t('noAccountsWithWindows')}</p>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </TabsContent>
                </Tabs>
            </CardContent>
        </Card>
    );
}